<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import {
  FAIR_PAGE_LOADER_DRAW_MS,
  FAIR_PAGE_LOADER_FILL_MS,
  FAIR_PAGE_LOADER_MONOGRAM_PATH,
  FAIR_PAGE_LOADER_SNAKE_RATIO,
  FAIR_PAGE_LOADER_SPIN_INTRO_MS,
  type FairPageLoaderMotion,
} from '@/ui/tokens/fair-page-loader.data'

interface FairPageLoaderMarkProps {
  /** Restart the draw / snake intro (e.g. each time loader opens). */
  replayKey?: number | string
  size?: number
  /** `draw` = route overlay loop · `spin` = short snake crawls FR contour */
  motion?: FairPageLoaderMotion
}

const props = withDefaults(defineProps<FairPageLoaderMarkProps>(), {
  replayKey: 0,
  size: 144,
  motion: 'draw',
})

const pathRef = ref<SVGPathElement | null>(null)
const pathLength = ref(0)
const ready = ref(false)

const markStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  '--fair-page-loader-length': String(pathLength.value),
  '--fair-page-loader-draw-ms': `${FAIR_PAGE_LOADER_DRAW_MS}ms`,
  '--fair-page-loader-spin-intro-ms': `${FAIR_PAGE_LOADER_SPIN_INTRO_MS}ms`,
  '--fair-page-loader-fill-ms': `${FAIR_PAGE_LOADER_FILL_MS}ms`,
}))

const markClass = computed(() => [
  `fair-page-loader__mark--${props.motion}`,
  { 'fair-page-loader__mark--ready': ready.value },
])

/** Normalized pathLength=1 → fixed dash math, no getTotalLength needed for crawl. */
const snakeDashStyle = computed(() => {
  const snake = FAIR_PAGE_LOADER_SNAKE_RATIO
  const gap = Math.max(1 - snake, 0.01)
  return {
    strokeDasharray: `${snake} ${gap}`,
    strokeDashoffset: 0,
  }
})

const drawDashStyle = computed(() =>
  pathLength.value
    ? { strokeDasharray: pathLength.value }
    : undefined,
)

async function prepare() {
  await nextTick()
  if (props.motion === 'spin') {
    // pathLength="1" — crawl keyframes use constant -1, ready immediately
    ready.value = true
    return
  }
  const el = pathRef.value
  if (!el) return
  pathLength.value = el.getTotalLength()
  ready.value = true
}

onMounted(() => {
  void prepare()
})

watch(
  () => [props.replayKey, props.motion] as const,
  async () => {
    ready.value = false
    await prepare()
  },
)
</script>

<template>
  <div
    class="fair-page-loader__mark"
    :class="markClass"
    :style="markStyle"
  >
    <div class="fair-page-loader__orbit">
      <svg
        class="fair-page-loader__svg"
        viewBox="0 0 57.2887 57.2887"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          v-if="motion === 'spin'"
          class="fair-page-loader__path fair-page-loader__path--ghost"
          :d="FAIR_PAGE_LOADER_MONOGRAM_PATH"
        />
        <path
          v-if="motion === 'spin'"
          class="fair-page-loader__path fair-page-loader__path--track"
          :d="FAIR_PAGE_LOADER_MONOGRAM_PATH"
        />
        <path
          ref="pathRef"
          class="fair-page-loader__path fair-page-loader__path--stroke"
          :d="FAIR_PAGE_LOADER_MONOGRAM_PATH"
          :pathLength="motion === 'spin' ? 1 : undefined"
          :style="motion === 'spin' ? snakeDashStyle : drawDashStyle"
        />
      </svg>
    </div>
  </div>
</template>
